import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { DocumentEntry, UpdateDocumentInput } from "@school-sales-ai/shared";
import {
  deactivateDocumentApi,
  deleteDocumentApi,
  listDocumentsApi,
  replaceDocumentFileApi,
  updateDocumentApi,
  uploadDocumentApi,
} from "@/lib/api";

export function useDocumentsQuery(): ReturnType<typeof useQuery<DocumentEntry[]>> {
  return useQuery<DocumentEntry[]>({
    queryKey: ["documents"],
    queryFn: listDocumentsApi,
  });
}

type UploadArgs = {
  file: File;
  title: string;
  description: string;
};

export function useUploadDocumentMutation(): ReturnType<
  typeof useMutation<DocumentEntry, Error, UploadArgs>
> {
  const queryClient = useQueryClient();

  return useMutation<DocumentEntry, Error, UploadArgs>({
    mutationFn: uploadDocumentApi,
    onSuccess: () => {
      toast.success(
        "Material subido. Estará disponible para el agente en menos de 5 minutos.",
      );
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al subir el material.");
    },
  });
}

type UpdateArgs = { id: string; data: UpdateDocumentInput };

export function useUpdateDocumentMutation(): ReturnType<
  typeof useMutation<DocumentEntry, Error, UpdateArgs>
> {
  const queryClient = useQueryClient();

  return useMutation<DocumentEntry, Error, UpdateArgs>({
    mutationFn: ({ id, data }) => updateDocumentApi(id, data),
    onSuccess: () => {
      toast.success("Material actualizado.");
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al actualizar el material.");
    },
  });
}

type SaveArgs = { id: string; data: UpdateDocumentInput | null; file: File | null };

/**
 * The edit modal's single save: metadata first (only when it changed — every
 * write moves `updatedAt`, which lets the agent re-send the material), then the
 * replacement file when one was picked. Two requests, one toast. When the
 * metadata lands and the file does not, the error says so, because the admin
 * would otherwise assume nothing was saved.
 */
export function useSaveDocumentMutation(): ReturnType<
  typeof useMutation<void, Error, SaveArgs>
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, SaveArgs>({
    mutationFn: async ({ id, data, file }) => {
      if (data) await updateDocumentApi(id, data);
      if (!file) return;
      try {
        await replaceDocumentFileApi(id, file);
      } catch (err) {
        const reason = err instanceof Error ? err.message : "error desconocido";
        throw new Error(
          data
            ? `Los datos se guardaron, pero el archivo no se reemplazó: ${reason}`
            : reason,
          { cause: err },
        );
      }
    },
    onSuccess: () => {
      toast.success("Material actualizado.");
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al guardar el material.");
    },
    // Either request may have landed; refresh the list in both outcomes.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useDeactivateDocumentMutation(): ReturnType<
  typeof useMutation<DocumentEntry, Error, string>
> {
  const queryClient = useQueryClient();

  return useMutation<DocumentEntry, Error, string>({
    mutationFn: deactivateDocumentApi,
    onSuccess: () => {
      toast.success("Material desactivado.");
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al desactivar el material.");
    },
  });
}

export function useDeleteDocumentMutation(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteDocumentApi,
    onSuccess: () => {
      toast.success("Material eliminado.");
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error) => {
      toast.error(error.message ?? "Error al eliminar el material.");
    },
  });
}
